import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import SwipeableViews from 'react-swipeable-views';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import CustomDialog from '../../common/components/CustomDialog';
import { useGetRoles } from '../../hooks/queries/useGetRoles';
import { useSetRoles } from '../../hooks/mutations/useSetRoles';
import { TabPanelProps } from '@material-ui/lab/TabPanel';
import { Operation, Resource, ROLES } from '../../common/constants';
import ConfirmationButtons from '../ConfirmationButtons/ConfirmationButtons';
import {
  PermissionInput,
  SetRolesInput,
} from '../../types/graphql-global-types';
import { SetRoles_setRoles_permissions } from '../../graphql/role/types/SetRoles';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import useStyles from './styles';
import PermissionsManager from '../PermissionsManager';

const intialRoles: SetRolesInput[] = [];

interface RoleManagerProps {
  open: boolean;
  onCloseClick?: () => void;
}

const RoleManager: FC<RoleManagerProps> = (props) => {
  const { open, onCloseClick } = props;
  const classes = useStyles();
  const theme = useTheme();

  const { canI } = useContext<IUserContext>(UserContext);
  const canEditRoles = canI(Operation.UPDATE, Resource.ROLES);

  const [roles, setRoles] = useState<(SetRolesInput | null)[] | null>(
    intialRoles,
  );
  const [tab, setTab] = useState<number>(0);

  const { data: rolesData, loading: getRolesLoading } = useGetRoles();
  const [updateRoles, { loading: setRolesLoading }] = useSetRoles();

  useEffect(() => {
    if (rolesData?.getRoles) {
      const _roles = rolesData?.getRoles?.map((role) => {
        if (!role) return;
        const { __typename, ...rest } = role;
        const permissions = role?.permissions?.map((permission) => {
          if (!permission) return;
          const { __typename, ...rest } = permission;
          return rest;
        }) as SetRoles_setRoles_permissions[];
        return { ...rest, permissions };
      }) as SetRolesInput[];
      setRoles(_roles);
    }
  }, [rolesData]);

  const handleClick = (
    role: string | null | undefined,
    permission: PermissionInput | null,
  ) => {
    setRoles(
      (_roles) =>
        _roles?.map((_data) => {
          if (_data?.role === role) {
            const permissions = _data?.permissions?.map((_permission) =>
              _permission?.resource === permission?.resource
                ? { ..._permission, ...permission }
                : _permission,
            );
            return { ..._data, permissions };
          }
          return _data;
        }) ?? [],
    );
  };

  const handleSave = async () => {
    const result = await updateRoles({
      variables: { inputs: roles },
    });
    if (result?.data?.setRoles && onCloseClick) onCloseClick();
  };

  const handleChangeTab = (event: ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  return (
    <CustomDialog
      loading={getRolesLoading || setRolesLoading}
      title="Gestion des rôles"
      open={open}
      className={classes.root}
      onClose={onCloseClick}
      actions={
        <ConfirmationButtons
          disableConfirm={
            !roles?.length ||
            !canEditRoles ||
            getRolesLoading ||
            setRolesLoading
          }
          handleCancel={onCloseClick}
          handleConfirm={handleSave}
        />
      }>
      {!roles?.length ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={240}
          height="100%">
          Pas de rôle pour l'instant.
        </Box>
      ) : (
        <>
          <AppBar position="static">
            <Tabs
              disabled={getRolesLoading}
              value={tab}
              onChange={handleChangeTab}
              scrollButtons="auto"
              variant="scrollable">
              {roles?.map(
                (role, index) =>
                  role?.role && (
                    <Tab
                      key={`roles-tab-${index}`}
                      label={ROLES[role?.role] ?? role?.role}
                      id={'tab' + index}
                    />
                  ),
              )}
            </Tabs>
          </AppBar>
          <SwipeableViews
            disabled={getRolesLoading}
            index={tab}
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}>
            {roles?.map((element, i) => {
              return (
                element?.role && (
                  <TabPanel
                    key={`roles-tab-${i}`}
                    value={`tab${tab}`}
                    index={i}>
                    <PermissionsManager
                      key={element?.role}
                      onChange={(permission) =>
                        handleClick(element?.role ?? '', permission)
                      }
                      permissions={element?.permissions}
                      loading={getRolesLoading}
                    />
                  </TabPanel>
                )
              );
            })}
          </SwipeableViews>
        </>
      )}
    </CustomDialog>
  );
};

function TabPanel(props: TabPanelProps & { index: number }) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== 'tab' + index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === 'tab' + index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default RoleManager;
